import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import clsx from 'clsx';
import { DefaultTooltip } from '@/components/tooltip/Tooltip';
import {
  ActivityLog,
  ActivityLogQueryParams,
  ActivityLogType,
} from '@/types/audit-logs.types';
import {
  useGetActivityLogsQuery,
  useExportActivityLogsCsvQuery,
} from '@/services/AuditLogsService';
import { PaginatedApiResponse } from '@/types/api.types';
import { activityTypeMap } from '@/constant';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDateTime } from '@/utils/Date';

const MANUAL_ANNOTATION_ACTIVITY_TYPES = new Set<ActivityLogType>([
  ActivityLogType.MANUAL_ANNOTATION_UPLOADED,
  ActivityLogType.MANUAL_ANNOTATION_RETRIEVED,
  ActivityLogType.MANUAL_ANNOTATION_DELETED,
  ActivityLogType.MANUAL_ANNOTATION_LISTED,
  ActivityLogType.MANUAL_ANNOTATION_PUBLISHED,
  ActivityLogType.MANUAL_ANNOTATION_EDITER,
]);

const ActivityLogs = () => {
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [searchParams] = useSearchParams();
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>(
    searchParams.get('activityType') ?? 'all',
  );
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [exportParams, setExportParams] = useState<Omit<
    ActivityLogQueryParams,
    'page' | 'limit'
  > | null>(null);

  const selectedActivityTypeLabel =
    activityTypeFilter !== 'all'
      ? (activityTypeMap[parseInt(activityTypeFilter) as ActivityLogType]
          ?.label ?? 'Unknown')
      : 'All Activities';

  const shouldShowActivityTypeTooltip =
    selectedActivityTypeLabel.length > 22 && activityTypeFilter !== 'all';

  // Export params: same as queryParams but without page/limit
  const exportQueryParams = {
    search: debouncedValue || undefined,
    userType: userTypeFilter !== 'all' ? userTypeFilter : undefined,
    activityType:
      activityTypeFilter !== 'all'
        ? (parseInt(activityTypeFilter) as ActivityLogType)
        : undefined,
    startDate:
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid()
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    endDate:
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid()
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
  };

  // Export CSV API call
  const { data: exportResponse, isLoading: isExportLoading } =
    useExportActivityLogsCsvQuery(exportParams ?? {}, { skip: !exportParams });

  useEffect(() => {
    if (exportResponse?.fileUrl) {
      const link = document.createElement('a');
      link.href = exportResponse.fileUrl;
      link.download = 'activity_logs.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportParams(null);
    }
  }, [exportResponse]);

  const handleExport = () => {
    setExportParams({ ...exportQueryParams, exportedAt: Date.now() });
  };

  // Query parameters for API call
  const queryParams: ActivityLogQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue || undefined,
    userType: userTypeFilter !== 'all' ? userTypeFilter : undefined,
    activityType:
      activityTypeFilter !== 'all'
        ? (parseInt(activityTypeFilter) as ActivityLogType)
        : undefined,
    startDate:
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid()
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
    endDate:
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid()
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : undefined,
  };

  const {
    data: logsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetActivityLogsQuery(queryParams) as {
    data: PaginatedApiResponse<ActivityLog[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  // Update pagination metadata
  useEffect(() => {
    if (logsResponse?.data) {
      handleTotalPages(logsResponse.total_pages || 1);
      handleTotalItems(logsResponse.total_items || 0);
    }
  }, [logsResponse, handleTotalPages, handleTotalItems]);

  // Refetch when filters or pagination change
  useEffect(() => {
    refetch();
  }, [
    userTypeFilter,
    activityTypeFilter,
    tempStartDate,
    tempEndDate,
    debouncedValue,
    pagination.pageIndex,
    pagination.pageSize,
    refetch,
  ]);

  // Reset pagination when filters change
  const handleFilterChange = (
    setter: (value: string) => void,
    value: string
  ) => {
    setter(value);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setUserTypeFilter('all');
    setActivityTypeFilter('all');
    setTempStartDate('');
    setTempEndDate('');
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row: ActivityLog) => row.user_name,
        id: 'user',
        header: 'User',
        enableSorting: false,
        cell: ({ row }: { row: { original: ActivityLog } }) => {
          const actor = (row.original.metadata as { actor?: unknown })
            ?.actor as
            | { id?: unknown; name?: unknown; email?: unknown }
            | undefined;

          const actorEmail =
            typeof actor?.email === 'string' ? actor.email.trim() : '';
          const actorId = typeof actor?.id === 'string' ? actor.id.trim() : '';

          const userName = (row.original.user_name || '').trim();
          const displayName = userName || actorEmail || '-';

          const isManualAnnotationLog = MANUAL_ANNOTATION_ACTIVITY_TYPES.has(
            row.original.activity_type
          );

          const shouldShowTooltip =
            isManualAnnotationLog && Boolean(actorId) && displayName !== '-';

          const tooltipContent = userName ? (
            <div className="flex flex-col">
              {actorEmail ? <span>{actorEmail}</span> : null}
              <span>{actorId}</span>
            </div>
          ) : (
            actorId
          );

          const rawRoles =
            (row.original.user as { roles?: unknown })?.roles ??
            (row.original as { roles?: unknown }).roles;

          let roleNames: string[] = [];

          if (Array.isArray(rawRoles)) {
            roleNames = (rawRoles as Array<{ display_name?: string } | string>)
              .map((role) =>
                typeof role === 'string' ? role : role?.display_name || ''
              )
              .filter((name) => name);
          } else if (typeof rawRoles === 'string') {
            try {
              const parsed = JSON.parse(rawRoles) as unknown;
              if (Array.isArray(parsed)) {
                roleNames = (
                  parsed as Array<{ display_name?: string } | string>
                )
                  .map((role) =>
                    typeof role === 'string' ? role : role?.display_name || ''
                  )
                  .filter((name) => name);
              }
            } catch {
              roleNames = rawRoles
                .split(',')
                .map((role) => role.trim())
                .filter((role) => role);
            }
          }

          const rolesLabel = roleNames.length
            ? roleNames.join(', ')
            : 'Unknown';

          return (
            <div className="flex items-center gap-4">
              <div className="rounded-full size-9 shrink-0 bg-gray-200 flex items-center justify-center">
                <KeenIcon
                  type="filled"
                  icon="user"
                  className="text-gray-500 text-xl"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                {shouldShowTooltip ? (
                  <DefaultTooltip
                    title={tooltipContent}
                    placement="top"
                    arrow
                    disableInteractive
                  >
                    <span className="w-fit">{displayName}</span>
                  </DefaultTooltip>
                ) : (
                  displayName
                )}
                <span className="text-2sm text-gray-700 font-normal">
                  {rolesLabel}
                </span>
              </div>
            </div>
          );
        },
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: ActivityLog) => row.created_at,
        id: 'timestamp',
        header: 'Timestamp',
        enableSorting: false,
        cell: ({ row }: { row: { original: ActivityLog } }) => {
          return formatDisplayDateTime(row.original.created_at, {
            includeSeconds: true,
          });
        },
        meta: {
          className: 'min-w-[200px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
      {
        accessorFn: (row: ActivityLog) => row.activity_type,
        id: 'activityType',
        header: 'Activity Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: ActivityLog } }) => {
          const activity = activityTypeMap[row.original.activity_type] || {
            label: 'Unknown',
            variant: 'light',
          };
          return (
            <span
              className={clsx(
                'text-nowrap badge badge-sm',
                `badge-${activity.variant}`
              )}
            >
              {activity.label}
            </span>
          );
        },
        meta: {
          className: 'min-w-[200px]',
        },
      },
      {
        accessorFn: (row: ActivityLog) => row.description,
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: ActivityLog } }) => {
          return row.original.description || 'No description';
        },
        meta: {
          className: 'min-w-[300px]',
          cellClassName: 'text-gray-800 font-normal',
        },
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    return logsResponse?.data || [];
  }, [logsResponse]);

  if (!hasPermission('activity_log_read')) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div className="text-center py-4 text-gray-500">
            You don't have permission to view activity logs.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Search Input */}
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              aria-label="Search logs"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                placeholder="Search logs"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search logs"
              />
            </label>
          </div>
          {/* User Type Filter */}
          <div className="flex-shrink-0">
            <select
              className="select select-sm w-28 h-8 sm:h-10 text-sm"
              value={userTypeFilter}
              onChange={(e) =>
                handleFilterChange(setUserTypeFilter, e.target.value)
              }
              aria-label="Filter by user type"
            >
              <option value="all">All Users</option>
              <option value="admin">Admin</option>
              <option value="boater">Boater</option>
              <option value="marina_manager">Marina</option>
            </select>
          </div>
          {/* Activity Type Filter */}
          <div className="flex-shrink-0">
            <DefaultTooltip
              key={activityTypeFilter}
              title={selectedActivityTypeLabel}
              placement="top"
              arrow
              disableHoverListener={!shouldShowActivityTypeTooltip}
              disableFocusListener={!shouldShowActivityTypeTooltip}
              disableTouchListener={!shouldShowActivityTypeTooltip}
            >
              <select
                className="select select-sm w-36 h-8 sm:h-10 text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                value={activityTypeFilter}
                onChange={(e) =>
                  handleFilterChange(setActivityTypeFilter, e.target.value)
                }
                aria-label="Filter by activity type"
                style={{
                  maxHeight: '150px !important',
                  overflowY: 'auto',
                }}
              >
                <option value="all">All Activities</option>
                {Object.entries(activityTypeMap)
                  .filter(
                    ([, { label }]) =>
                      label !== 'User Deleted' && label !== 'Export PDF'
                  )
                  .map(([key, { label }]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
              </select>
            </DefaultTooltip>
          </div>
          {/* Date Range Picker */}
          <div className="flex-shrink-0">
            <DateRangePicker
              startDateName="startDate"
              endDateName="endDate"
              isMandatory={false}
              value={[tempStartDate, tempEndDate]}
              onChange={([newStartDate, newEndDate]) => {
                handleDateRangeChange(
                  newStartDate as string,
                  newEndDate as string
                );
              }}
              allowPastDates={true}
              allowFutureDates={false}
              className="w-full sm:w-64 h-8 sm:h-10 text-sm"
            />
          </div>
          {/* Export CSV Button */}
          {hasPermission('activity_log_export') && (
            <button
              className="btn btn-sm btn-primary flex items-center gap-2 h-8 sm:h-10"
              onClick={handleExport}
              aria-label={
                isExportLoading
                  ? 'Exporting... filtered logs as CSV'
                  : 'Export CSV filtered logs'
              }
              title="Export logs as CSV"
              disabled={isExportLoading}
            >
              <KeenIcon
                type="solid"
                icon="export"
                className="text-white"
                aria-hidden="true"
              />
              {isExportLoading ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
        </div>

        {/* Clear Filters Button */}
        {(userTypeFilter !== 'all' ||
          activityTypeFilter !== 'all' ||
          tempStartDate ||
          tempEndDate ||
          searchTerm) && (
          <div className="flex items-center">
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          </div>
        )}
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div className="text-center py-4">Loading logs...</div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500">
            Failed to load logs. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems,
              totalPages: totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'timestamp', desc: true }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={
              userTypeFilter !== 'all' ||
              activityTypeFilter !== 'all' ||
              tempStartDate !== '' ||
              tempEndDate !== '' ||
              searchTerm !== ''
            }
          />
        )}
      </div>
    </div>
  );
};

export { ActivityLogs };
